import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  Box,
  Button,
  CircularProgress,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAuth } from '../../contexts/AuthContext';
import { usePendingRequests } from '../../contexts/PendingRequestsContext';
import { useTaskNotifications } from '../../contexts/TaskNotificationContext';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, orderBy, limit, doc, updateDoc, getDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import RegisterModal from '../schedule/RegisterModal';

export default function NotificationBell() {
  const { currentUser } = useAuth();
  const { pendingRequestsCount } = usePendingRequests();
  const { taskNotifications, unreadTaskCount } = useTaskNotifications();
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [loadingModal, setLoadingModal] = useState(false);
  const [scheduleStatuses, setScheduleStatuses] = useState({});
  const [approvalDialog, setApprovalDialog] = useState({ open: false, notification: null, action: null });
  const [approvalNote, setApprovalNote] = useState('');

  // States để lưu trữ requests từ các collections
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [lateRequests, setLateRequests] = useState([]);
  const [editRequests, setEditRequests] = useState([]);
  const [realNotifications, setRealNotifications] = useState([]);

  useEffect(() => {
    if (!currentUser) return;

    // Fetch notifications from notifications collection
    const notificationsUnsubscribe = onSnapshot(
      query(
        collection(db, 'notifications'),
        where('userId', '==', currentUser.uid),
        orderBy('timestamp', 'desc'),
        limit(10)
      ),
      (snapshot) => {
        const notifications = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRealNotifications(notifications);
      },
      (error) => {
        console.error('Error fetching notifications:', error);
      }
    );

    // Fetch pending leave requests directly from schedules collection for admin users
    let leaveRequestsUnsubscribe = null;
    if (currentUser.role === 'admin') {
      leaveRequestsUnsubscribe = onSnapshot(
        query(
          collection(db, 'schedules'),
          where('status', '==', 'on_leave_pending'),
          orderBy('createdAt', 'desc')
        ),
        (snapshot) => {
          const leaveRequests = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          setLeaveRequests(leaveRequests);

          // Update schedule statuses
          const newScheduleStatuses = {};
          leaveRequests.forEach(request => {
            newScheduleStatuses[request.id] = request.status;
          });
          setScheduleStatuses(prev => ({ ...prev, ...newScheduleStatuses }));
        },
        (error) => {
          console.error('Error fetching leave requests:', error);
        }
      );
    }

    // Fetch pending late requests for admin users
    let lateRequestsUnsubscribe = null;
    if (currentUser.role === 'admin') {
      lateRequestsUnsubscribe = onSnapshot(
        query(
          collection(db, 'late_requests'),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        ),
        (snapshot) => {
          const lateRequests = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          setLateRequests(lateRequests);
        },
        (error) => {
          console.error('Error fetching late requests:', error);
        }
      );
    }

    // Fetch pending edit requests for admin users
    let editRequestsUnsubscribe = null;
    if (currentUser.role === 'admin') {
      editRequestsUnsubscribe = onSnapshot(
        query(
          collection(db, 'edit_requests'),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        ),
        (snapshot) => {
          const editRequests = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          setEditRequests(editRequests);
        },
        (error) => {
          console.error('Error fetching edit requests:', error);
        }
      );
    }

    return () => {
      notificationsUnsubscribe();
      if (leaveRequestsUnsubscribe) leaveRequestsUnsubscribe();
      if (lateRequestsUnsubscribe) lateRequestsUnsubscribe();
      if (editRequestsUnsubscribe) editRequestsUnsubscribe();
    };
  }, [currentUser]);

  // Combine tất cả notifications
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin') return;

    // Create virtual notifications for leave requests
    const leaveVirtualNotifications = leaveRequests.map(request => ({
      id: `leave_${request.id}`,
      message: `${request.userName} vừa gửi yêu cầu xin nghỉ ${request.shiftType} ngày ${request.date}.`,
      link: '/admin/schedule',
      type: 'leave_request',
      scheduleId: request.id,
      isRead: false,
      timestamp: request.createdAt || new Date(),
      isVirtual: true
    }));

    // Create virtual notifications for late requests
    const lateVirtualNotifications = lateRequests.map(request => ({
      id: `late_${request.id}`,
      message: `${request.userName} vừa gửi yêu cầu đi muộn ${request.requestType === 'minutes' ? `${request.lateMinutes} phút` : `đến ${request.lateTime}`} cho ${request.shiftType} ngày ${request.date}.`,
      link: '/admin/requests',
      type: 'late_request',
      scheduleId: request.id,
      isRead: false,
      timestamp: request.createdAt || new Date(),
      isVirtual: true
    }));

    // Create virtual notifications for edit requests
    const editVirtualNotifications = editRequests.map(request => ({
      id: `edit_${request.id}`,
      message: `${request.userName} vừa gửi yêu cầu chỉnh sửa ca làm việc ${request.shiftType} ngày ${request.date}.`,
      link: '/admin/requests',
      type: 'edit_request',
      scheduleId: request.id,
      isRead: false,
      timestamp: request.createdAt || new Date(),
      isVirtual: true
    }));

    // Convert task notifications to notification format
    const taskNotificationsFormatted = taskNotifications.map(taskNotif => ({
      id: taskNotif.id,
      type: taskNotif.type,
      message: taskNotif.message,
      timestamp: taskNotif.createdAt || new Date(),
      isRead: taskNotif.read || false,
      isVirtual: false,
      taskId: taskNotif.taskId,
      taskTitle: taskNotif.taskTitle
    }));

    // Combine all notifications
    const allNotifications = [
      ...leaveVirtualNotifications,
      ...lateVirtualNotifications,
      ...editVirtualNotifications,
      ...realNotifications,
      ...taskNotificationsFormatted
    ];

    setNotifications(allNotifications);
  }, [leaveRequests, lateRequests, editRequests, realNotifications, taskNotifications, currentUser]);

  // Tính toán unread count cho tất cả user
  useEffect(() => {
    if (!currentUser) return;

    if (currentUser.role === 'admin') {
      // Admin: chỉ đếm thông báo thật (không bao gồm virtual requests)
      const realUnread = realNotifications.filter(n => !n.isRead).length;
      setUnreadCount(realUnread);
    } else {
      // Non-admin: đếm tất cả thông báo thật chưa đọc
      const realUnread = realNotifications.filter(n => !n.isRead).length;
      setUnreadCount(realUnread);
      // Cũng cập nhật notifications cho non-admin
      setNotifications(realNotifications);
    }
  }, [realNotifications, currentUser]);

  // Đánh dấu tất cả đã đọc
  const markAllAsRead = async () => {
    const unreadNotifs = notifications.filter(n => !n.isRead && !n.isVirtual);
    if (unreadNotifs.length === 0) return;

    const batch = writeBatch(db);
    unreadNotifs.forEach(notif => {
      const notifRef = doc(db, 'notifications', notif.id);
      batch.update(notifRef, { isRead: true });
    });

    try {
      await batch.commit();
      // Cập nhật local state
      setRealNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
      setNotifications(prev => prev.map(notif =>
        notif.isVirtual ? notif : { ...notif, isRead: true }
      ));
      setUnreadCount(0);
    } catch (error) {
      console.error('Lỗi khi đánh dấu đã đọc:', error);
    }
  };

  // Đánh dấu một thông báo đã đọc
  const markSingleAsRead = async (notificationId) => {
    try {
      const notifRef = doc(db, 'notifications', notificationId);
      await updateDoc(notifRef, { isRead: true });

      // Cập nhật local state
      setRealNotifications(prev => prev.map(notif =>
        notif.id === notificationId ? { ...notif, isRead: true } : notif
      ));
      setNotifications(prev => prev.map(notif =>
        notif.id === notificationId ? { ...notif, isRead: true } : notif
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Lỗi khi đánh dấu đã đọc:', error);
    }
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    // Không tự động đánh dấu đã đọc nữa - để user chủ động bấm nút
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = async (action, scheduleId, notificationId) => {
    if (!currentUser || currentUser.role !== 'admin') return;

    const actorInfo = {
      uid: currentUser.uid,
      name: currentUser.displayName
    };

    try {
      let newStatus = '';
      if (action === 'approve') {
        if (window.confirm("Bạn có chắc muốn đồng ý yêu cầu xin nghỉ này?")) {
          newStatus = 'on_leave_approved';
          const scheduleRef = doc(db, 'schedules', scheduleId);

          await updateDoc(scheduleRef, {
            status: newStatus,
            approvedBy: actorInfo,
            approvedAt: serverTimestamp()
          });

          // Cập nhật trạng thái local ngay lập tức
          setScheduleStatuses(prev => ({
            ...prev,
            [scheduleId]: newStatus
          }));

          // Cập nhật thông báo local ngay lập tức
          setNotifications(prev => prev.map(notif =>
            notif.scheduleId === scheduleId || notif.id === `leave_${scheduleId}`
              ? { ...notif, isRead: true }
              : notif
          ));

          // Đánh dấu thông báo đã đọc (nếu có notificationId)
          if (notificationId) {
            const notifRef = doc(db, 'notifications', notificationId);
            await updateDoc(notifRef, { isRead: true });
          }

          // Context sẽ tự động cập nhật số lượng yêu cầu chưa xử lý
        }
      } else if (action === 'deny') {
        if (window.confirm("Bạn có chắc muốn từ chối yêu cầu xin nghỉ này?")) {
          newStatus = 'working';
          const scheduleRef = doc(db, 'schedules', scheduleId);

          await updateDoc(scheduleRef, {
            status: newStatus,
            leaveReason: null,
            approvedBy: actorInfo,
            approvedAt: serverTimestamp()
          });

          // Cập nhật trạng thái local ngay lập tức
          setScheduleStatuses(prev => ({
            ...prev,
            [scheduleId]: newStatus
          }));

          // Cập nhật thông báo local ngay lập tức
          setNotifications(prev => prev.map(notif =>
            notif.scheduleId === scheduleId || notif.id === `leave_${scheduleId}`
              ? { ...notif, isRead: true }
              : notif
          ));

          // Đánh dấu thông báo đã đọc (nếu có notificationId)
          if (notificationId) {
            const notifRef = doc(db, 'notifications', notificationId);
            await updateDoc(notifRef, { isRead: true });
          }

          // Context sẽ tự động cập nhật số lượng yêu cầu chưa xử lý
        }
      }
    } catch (error) {
      console.error("Lỗi khi xử lý yêu cầu:", error);
    }
  };

  // Xử lý phê duyệt/từ chối yêu cầu đi muộn và chỉnh sửa
  const handleRequestApproval = async (notification, approved) => {
    if (!currentUser) return;

    try {
      let requestRef;
      let updateData = {
        status: approved ? 'approved' : 'rejected',
        approvedBy: {
          uid: currentUser.uid,
          name: currentUser.displayName
        },
        lastUpdatedBy: {
          uid: currentUser.uid,
          name: currentUser.displayName
        },
        approvalNote: approvalNote.trim() || null,
        approvedAt: serverTimestamp()
      };

      if (notification.type === 'late_request') {
        requestRef = doc(db, 'late_requests', notification.scheduleId);
      } else if (notification.type === 'edit_request') {
        requestRef = doc(db, 'edit_requests', notification.scheduleId);
      } else if (notification.type === 'leave_request') {
        requestRef = doc(db, 'schedules', notification.scheduleId);
        updateData.status = approved ? 'on_leave_approved' : 'working';
        if (!approved) {
          updateData.leaveReason = null; // Xóa lý do nghỉ nếu từ chối
        }
      }

      await updateDoc(requestRef, updateData);

      // Đánh dấu thông báo đã đọc (nếu là thông báo thật)
      if (!notification.isVirtual) {
        const notifRef = doc(db, 'notifications', notification.id);
        await updateDoc(notifRef, { isRead: true });
      }

      // Cập nhật thông báo local
      setNotifications(prev => prev.map(notif =>
        notif.id === notification.id
          ? { ...notif, isRead: true }
          : notif
      ));

      // Context sẽ tự động cập nhật số lượng yêu cầu chưa xử lý

      closeApprovalDialog();
    } catch (error) {
      console.error('Lỗi khi phê duyệt yêu cầu:', error);
      alert('Có lỗi xảy ra khi phê duyệt yêu cầu. Vui lòng thử lại.');
    }
  };

  const openApprovalDialog = (notification, action) => {
    setApprovalDialog({ open: true, notification, action });
    setApprovalNote('');
  };

  const closeApprovalDialog = () => {
    setApprovalDialog({ open: false, notification: null, action: null });
    setApprovalNote('');
  };

  const handleOpenEditModal = async (scheduleId) => {
    setLoadingModal(true);
    handleClose();
    const scheduleRef = doc(db, 'schedules', scheduleId);
    const docSnap = await getDoc(scheduleRef);
    if (docSnap.exists()) {
      setSelectedRegistration({ id: docSnap.id, ...docSnap.data() });
      setIsModalOpen(true);
    } else {
      console.error("Không tìm thấy lịch làm việc để chỉnh sửa!");
    }
    setLoadingModal(false);
  };

  // Hàm callback khi modal đổi ca thành công
  const handleEditSuccess = (scheduleId, newStatus) => {
    // Cập nhật trạng thái local ngay lập tức
    setScheduleStatuses(prev => ({
      ...prev,
      [scheduleId]: newStatus || 'working'
    }));

    // Cập nhật thông báo local ngay lập tức
    setNotifications(prev => prev.map(notif =>
      notif.scheduleId === scheduleId
        ? { ...notif, isRead: true }
        : notif
    ));
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'leave_request':
        return '📝';
      case 'leave_approved':
        return '✅';
      case 'leave_denied':
        return '❌';
      case 'schedule_change':
        return '🔄';
      case 'late_request':
        return '⏰';
      case 'edit_request':
        return '✏️';
      case 'late_request_response':
        return '⏰';
      case 'edit_request_response':
        return '✏️';
      case 'task_assigned':
        return '📋';
      case 'task_updated':
        return '📊';
      case 'task_completed':
        return '🎯';
      case 'overtime_request':
      case 'overtime_request_response':
        return '🕒';
      case 'early_leave_request':
      case 'early_leave_request_response':
        return '🏃';
      default:
        return '📢';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'leave_request':
        return 'warning';
      case 'leave_approved':
        return 'success';
      case 'leave_denied':
        return 'error';
      case 'schedule_change':
        return 'info';
      case 'late_request':
        return 'info';
      case 'edit_request':
        return 'primary';
      case 'late_request_response':
        return 'success';
      case 'edit_request_response':
        return 'success';
      case 'task_assigned':
        return 'primary';
      case 'task_updated':
        return 'info';
      case 'task_completed':
        return 'success';
      case 'overtime_request':
        return 'secondary';
      case 'overtime_request_response':
        return 'success';
      case 'early_leave_request':
        return 'info';
      case 'early_leave_request_response':
        return 'success';
      default:
        return 'default';
    }
  };

  const renderNotificationActions = (notification) => {
    // Chỉ hiển thị nút hành động khi người dùng hiện tại là admin
    if (currentUser?.role !== 'admin') return null;

    // Xử lý yêu cầu xin nghỉ
    if (notification.type === 'leave_request' && notification.scheduleId) {
      const currentScheduleStatus = scheduleStatuses[notification.scheduleId];
      if (currentScheduleStatus === 'on_leave_pending') {
        return (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={() => handleAction('approve', notification.scheduleId, notification.isVirtual ? null : notification.id)}
            >
              Đồng ý
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<CloseIcon />}
              onClick={() => handleAction('deny', notification.scheduleId, notification.isVirtual ? null : notification.id)}
            >
              Từ chối
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => handleOpenEditModal(notification.scheduleId)}
            >
              Đổi ca
            </Button>
          </Stack>
        );
      } else {
        return (
          <Chip
            label={currentScheduleStatus === 'on_leave_approved' ? 'Đã được duyệt' :
              currentScheduleStatus === 'working' ? 'Đã bị từ chối' : 'Đã được xử lý'}
            size="small"
            color={currentScheduleStatus === 'on_leave_approved' ? 'success' :
              currentScheduleStatus === 'working' ? 'error' : 'default'}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        );
      }
    }

    // Xử lý yêu cầu đi muộn
    if (notification.type === 'late_request' && notification.scheduleId) {
      return (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckIcon />}
            onClick={() => openApprovalDialog(notification, 'approve')}
          >
            Phê duyệt
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            startIcon={<CloseIcon />}
            onClick={() => openApprovalDialog(notification, 'reject')}
          >
            Từ chối
          </Button>
        </Stack>
      );
    }

    // Xử lý yêu cầu chỉnh sửa ca làm việc
    if (notification.type === 'edit_request' && notification.scheduleId) {
      return (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckIcon />}
            onClick={() => openApprovalDialog(notification, 'approve')}
          >
            Phê duyệt
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            startIcon={<CloseIcon />}
            onClick={() => openApprovalDialog(notification, 'reject')}
          >
            Từ chối
          </Button>
        </Stack>
      );
    }

    return null;
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen}>
        <Badge badgeContent={currentUser?.role === 'admin' ? (pendingRequestsCount + unreadTaskCount) : (unreadCount + unreadTaskCount)} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { width: 400, maxHeight: 500 }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">
              Thông báo {unreadCount > 0 ? `(${unreadCount} mới)` : ''}
            </Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={markAllAsRead}>
                Đánh dấu tất cả đã đọc
              </Button>
            )}
          </Box>

          {notifications.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Không có thông báo nào
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map((notification, index) => (
                <Box key={notification.id}>
                  <ListItem sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
                    borderRadius: 1,
                    mb: 1
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                      <Typography variant="h6" sx={{ mr: 1 }}>
                        {getNotificationIcon(notification.type)}
                      </Typography>
                      <Chip
                        label={notification.type === 'leave_request' ? 'Yêu cầu xin nghỉ' :
                          notification.type === 'leave_approved' ? 'Đã duyệt' :
                            notification.type === 'leave_denied' ? 'Đã từ chối' :
                              notification.type === 'schedule_change' ? 'Thay đổi lịch' :
                                notification.type === 'late_request' ? 'Yêu cầu đi muộn' :
                                  notification.type === 'edit_request' ? 'Yêu cầu chỉnh sửa' :
                                    notification.type === 'late_request_response' ? 'Phản hồi đi muộn' :
                                      notification.type === 'edit_request_response' ? 'Phản hồi chỉnh sửa' :
                                        notification.type === 'task_assigned' ? 'Giao việc' :
                                          notification.type === 'task_updated' ? 'Cập nhật công việc' :
                                            notification.type === 'task_completed' ? 'Hoàn thành công việc' :
                                              notification.type === 'overtime_request' ? 'Đăng ký làm thêm giờ' :
                                                notification.type === 'overtime_request_response' ? 'Phản hồi làm thêm giờ' :
                                                  notification.type === 'early_leave_request' ? 'Yêu cầu về sớm' :
                                                    notification.type === 'early_leave_request_response' ? 'Phản hồi về sớm' :
                                                      'Thông báo'}
                        size="small"
                        color={getNotificationColor(notification.type)}
                        variant="outlined"
                      />
                      {!notification.isRead && (
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          ml: 'auto'
                        }} />
                      )}
                      {/* Debug info */}
                      <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                        {notification.isRead ? 'Đã đọc' : 'Chưa đọc'} | {notification.type}
                      </Typography>
                    </Box>

                    <ListItemText
                      primary={notification.message}
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {notification.timestamp && formatDistanceToNow(notification.timestamp.toDate(), {
                            addSuffix: true,
                            locale: vi
                          })}
                        </Typography>
                      }
                      sx={{ width: '100%' }}
                    />

                    {renderNotificationActions(notification)}

                    {/* Nút Đã đọc và Xem sau cho thông báo thường (không phải virtual) */}
                    {!notification.isVirtual && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1, width: '100%' }}>
                        {!notification.isRead && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() => markSingleAsRead(notification.id)}
                          >
                            ✓ Đã đọc
                          </Button>
                        )}
                      </Stack>
                    )}
                  </ListItem>

                  {index < notifications.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}
        </Box>
      </Popover>

      {isModalOpen && selectedRegistration && (
        <RegisterModal
          open={isModalOpen}
          handleClose={() => {
            setIsModalOpen(false);
            setSelectedRegistration(null);
          }}
          date={new Date(selectedRegistration.date + 'T00:00:00')}
          initialShift={selectedRegistration.shiftType}
          existingRegistration={selectedRegistration}
          onRegisterSuccess={() => {
            // Gọi callback để cập nhật trạng thái local
            handleEditSuccess(selectedRegistration.id, 'working');
            setIsModalOpen(false);
            setSelectedRegistration(null);
          }}
        />
      )}

      {/* Approval Dialog */}
      <Dialog open={approvalDialog.open} onClose={closeApprovalDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {approvalDialog.action === 'approve' ? 'Phê duyệt yêu cầu' : 'Từ chối yêu cầu'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {approvalDialog.notification?.message}
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Ghi chú (tùy chọn)"
            value={approvalNote}
            onChange={(e) => setApprovalNote(e.target.value)}
            placeholder="Nhập ghi chú nếu cần..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApprovalDialog}>Hủy</Button>
          <Button
            onClick={() => handleRequestApproval(approvalDialog.notification, false)}
            color="error"
            variant="outlined"
          >
            Từ chối
          </Button>
          <Button
            onClick={() => handleRequestApproval(approvalDialog.notification, true)}
            color="success"
            variant="contained"
          >
            Phê duyệt
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
